import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { CheckCircle, Save } from '@mui/icons-material';
import configurationService from '../services/configurationService';

const FORMAT_OPTIONS = [
  {
    id: 'simple',
    title: 'Número consecutivo',
    description: 'La opción más sencilla y recomendada.',
    format: '{prefijo}{numero:06d}',
    example: 'V-000001'
  },
  {
    id: 'year',
    title: 'Año + número',
    description: 'Permite reconocer rápidamente el año.',
    format: '{prefijo}{año}-{numero:06d}',
    example: 'V-2026-000001'
  },
  {
    id: 'date',
    title: 'Fecha completa + número',
    description: 'Incluye año, mes y día de la venta.',
    format: '{prefijo}{año}{mes}{dia}-{numero:04d}',
    example: 'V-20260728-0001'
  }
];

const getOptionId = (format) => (
  FORMAT_OPTIONS.find((option) => option.format === format)?.id || 'custom'
);

const SaleNumberingConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedOption, setSelectedOption] = useState('simple');
  const [config, setConfig] = useState({
    prefijo: 'V-',
    formato: FORMAT_OPTIONS[0].format,
    numero_actual: '1'
  });

  const preview = useMemo(
    () => configurationService.generatePreview({
      prefijo: config.prefijo || '',
      formato: config.formato || FORMAT_OPTIONS[0].format,
      numero_actual: config.numero_actual || '1'
    }),
    [config]
  );

  const loadConfig = async () => {
    setLoading(true);
    const result = await configurationService.getSalesConfiguration();

    if (result.success) {
      const salesConfig = result.data;
      const loadedFormat = salesConfig.formato?.valor || FORMAT_OPTIONS[0].format;
      setConfig({
        prefijo: salesConfig.prefijo?.valor || 'V-',
        formato: loadedFormat,
        numero_actual: salesConfig.numero_actual?.valor || '1'
      });
      setSelectedOption(getOptionId(loadedFormat));
    } else {
      setMessage({ type: 'error', text: result.message });
    }

    setLoading(false);
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const selectFormat = (option) => {
    setSelectedOption(option.id);
    setConfig((current) => ({ ...current, formato: option.format }));
  };

  const handleSave = async () => {
    if (!config.formato.includes('{numero')) {
      setMessage({
        type: 'error',
        text: 'El formato debe incluir un número consecutivo.'
      });
      return;
    }

    setSaving(true);
    const result = await configurationService.updateSalesConfiguration({
      prefijo: config.prefijo,
      formato: config.formato
    });

    if (result.success) {
      setMessage({ type: 'success', text: 'Configuración guardada correctamente.' });
      await loadConfig();
    } else {
      setMessage({ type: 'error', text: result.message });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Box className="d-flex justify-center align-center py-lg">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {message.text && (
        <Alert
          severity={message.type}
          onClose={() => setMessage({ type: '', text: '' })}
          className="mb-md"
        >
          {message.text}
        </Alert>
      )}

      <CardContent>
        <Typography variant="h6" className="mb-xs font-semibold">
          Numeración de ventas
        </Typography>
        <Typography variant="body2" color="text.secondary" className="mb-lg">
          Elegí cómo querés identificar los tickets y ventas internas.
        </Typography>

        <Typography variant="subtitle1" className="mb-sm font-semibold">
          1. Elegí un estilo
        </Typography>
        <Grid container spacing={2} className="mb-lg">
          {FORMAT_OPTIONS.map((option) => {
            const selected = selectedOption === option.id;
            return (
              <Grid item xs={12} md={4} key={option.id}>
                <Paper
                  role="button"
                  tabIndex={0}
                  onClick={() => selectFormat(option)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') selectFormat(option);
                  }}
                  sx={{
                    p: 2,
                    height: '100%',
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    bgcolor: selected ? 'action.selected' : 'background.paper'
                  }}
                >
                  <Box className="d-flex justify-between align-center mb-xs">
                    <Typography fontWeight={700}>{option.title}</Typography>
                    {selected && <CheckCircle color="primary" fontSize="small" />}
                  </Box>
                  <Typography variant="body2" color="text.secondary" className="mb-sm">
                    {option.description}
                  </Typography>
                  <Typography fontFamily="monospace" color="primary.main" fontWeight={700}>
                    {option.example}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}

          <Grid item xs={12} md={4}>
            <Paper
              role="button"
              tabIndex={0}
              onClick={() => setSelectedOption('custom')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setSelectedOption('custom');
              }}
              sx={{
                p: 2,
                height: '100%',
                cursor: 'pointer',
                border: '2px solid',
                borderColor: selectedOption === 'custom' ? 'primary.main' : 'divider',
                bgcolor: selectedOption === 'custom' ? 'action.selected' : 'background.paper'
              }}
            >
              <Box className="d-flex justify-between align-center mb-xs">
                <Typography fontWeight={700}>Formato personalizado</Typography>
                {selectedOption === 'custom' && (
                  <CheckCircle color="primary" fontSize="small" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                Para configuraciones especiales o varias sucursales.
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Typography variant="subtitle1" className="mb-sm font-semibold">
          2. Escribí el prefijo
        </Typography>
        <TextField
          fullWidth
          label="Letras que aparecen antes del número"
          value={config.prefijo}
          onChange={(event) => setConfig({ ...config, prefijo: event.target.value })}
          placeholder="V-"
          helperText='Recomendado: “V-”. También podés usar “TICKET-” o dejarlo vacío.'
          sx={{ mb: 3 }}
        />

        {selectedOption === 'custom' && (
          <TextField
            fullWidth
            label="Formato avanzado"
            value={config.formato}
            onChange={(event) => setConfig({ ...config, formato: event.target.value })}
            helperText="Variables: {prefijo}, {año}, {mes}, {dia}, {numero:04d}, {numero:06d}"
            sx={{ mb: 3 }}
          />
        )}

        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: 'success.lighter',
            border: '2px solid',
            borderColor: 'success.main'
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" className="mb-sm">
            Así se verá la próxima venta
          </Typography>
          <Typography
            variant="h4"
            className="font-bold"
            sx={{ fontFamily: '"Courier New", monospace', color: 'success.dark' }}
          >
            {preview}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Próximo consecutivo: {config.numero_actual}
          </Typography>
        </Paper>
      </CardContent>

      <CardContent>
        <Box className="d-flex justify-end">
          <Button
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar numeración'}
          </Button>
        </Box>
      </CardContent>
    </Box>
  );
};

export default SaleNumberingConfig;
